// `configureCodingData` — the injected-config seam (pilot D1, OpenSpec
// `monorepo-wave-2-coding-pilot`; the W3d/`configureWorkspaceData` pattern).
// Packages are variant-agnostic and store-free (G1/F5): the app injects the
// facts the clinical-coding transport needs but cannot know (`clinicalCoding
// Transport` below — the builder itself is this package's, in
// `data/api/clinicalCodingHTTP.ts`) and the app's queryClient (for module-scope
// invalidators) at bootstrap (`src/utils/coding-data-wiring.ts`).
// The scribe client family is NOT on this seam — `data/api` reads it from the
// `@heidi/api/client` registry (E5, ARCFE-805).
import type { QueryClient } from '@tanstack/react-query';

/**
 * Minimal structural view of the clinical-coding axios client — kept
 * structural so this package's public fn signatures never name axios types
 * (declaration portability in the standalone build).
 */
export interface CodingHttpClient {
  get<T>(url: string, config?: { signal?: AbortSignal }): Promise<{ data: T }>;
  post<T>(
    url: string,
    body?: unknown,
    config?: { signal?: AbortSignal; responseType?: 'json' | 'text' | 'blob' }
  ): Promise<{ data: T }>;
  put<T>(url: string, body?: unknown): Promise<{ data: T }>;
  patch<T>(url: string, body?: unknown): Promise<{ data: T }>;
  delete<T>(
    url: string,
    config?: { headers?: Record<string, string> }
  ): Promise<{ data: T }>;
}

/**
 * A mutable outbound header bag — structural, so the ports below never name
 * axios's types either.
 */
export type ClinicalCodingRequestHeaders = Record<string, unknown>;

/**
 * The app-owned facts the clinical-coding transport's interceptors read. One
 * port per fact, each a getter rather than a value: the base URL resolves
 * through the region (and through the store), the workspace id and app version
 * change while the client lives, and none of them may be read at import time.
 */
export interface ClinicalCodingTransportPorts {
  /** Region-resolved SCC base URL; null when the region has no coding API. */
  getBaseUrl: () => string | null;
  /** Desktop build or web — read ONCE per built client, as the app did. */
  isDesktopClient: () => boolean;
  /** App version for `X-Client-Version`; the header is omitted when absent. */
  getClientVersion: () => string | undefined;
  /** Active workspace id; the header is omitted when there is none. */
  getWorkspaceId: () => string | null;
  /** Stamps the FE↔BE tracing headers onto the bag, in place. */
  applyTracingHeaders: (headers: ClinicalCodingRequestHeaders) => void;
  /**
   * The app's shared rejected-error handler, tagged with this client's name.
   * Every non-401 goes here; a 401 must NOT, because this handler can end the
   * Heidi session and an SCC token failure is not a Heidi logout.
   */
  handleRejectedError: (error: unknown) => unknown;
}

export interface CodingDataConfig {
  /** App-owned facts the package's clinical-coding transport reads per request. */
  clinicalCodingTransport: ClinicalCodingTransportPorts;
  /** The app's queryClient singleton — module-scope invalidators run against it. */
  queryClient: QueryClient;
  /**
   * Raw `NEXT_PUBLIC_ENVIRONMENT` value. Injected rather than read here (F5):
   * the RAW string, deliberately not a normalized enum — the self-serve
   * allowlist fails closed on unrecognised values, which a normalizer that
   * defaults to "dev" would defeat.
   */
  environment: string;
}

let config: CodingDataConfig | null = null;

export const configureCodingData = (next: CodingDataConfig): void => {
  config = next;
};

/** Fail loudly on unconfigured use — never silently degrade. */
export const getCodingDataConfig = (): CodingDataConfig => {
  if (!config) {
    throw new Error(
      '[coding/data] not configured — app bootstrap must call configureCodingData() ' +
        '(see src/utils/coding-data-wiring.ts); tests exercising real coding data ' +
        'modules must configure a test double first.'
    );
  }
  return config;
};
